<template>
  <div class="activacion-modulos-container">
    <!-- Header -->
    <div class="page-header">
      <div class="header-content">
        <h1 class="page-title">
          <i class="fas fa-toggle-on me-2"></i>
          Activación de Módulos
        </h1>
        <p class="page-subtitle">
          <i class="fas fa-shield-alt me-1"></i>
          Controla qué módulos están disponibles para las empresas
        </p>
      </div>
      <button @click="refreshModules" class="btn-secondary-custom">
        <i class="fas fa-sync-alt"></i>
        Actualizar
      </button>
    </div>

    <!-- Estadísticas -->
    <div class="stats-grid" v-if="stats">
      <div class="stat-card stat-total">
        <div class="stat-number">{{ stats.total_modules || 0 }}</div>
        <div class="stat-label">Total Módulos</div>
      </div>
      <div class="stat-card stat-active">
        <div class="stat-number">{{ stats.active_modules || 0 }}</div>
        <div class="stat-label">Módulos Activos</div>
      </div>
      <div class="stat-card stat-inactive">
        <div class="stat-number">{{ stats.inactive_modules || 0 }}</div>
        <div class="stat-label">Módulos Inactivos</div>
      </div>
      <div class="stat-card stat-core">
        <div class="stat-number">{{ stats.core_modules || 0 }}</div>
        <div class="stat-label">Módulos Core</div>
      </div>
    </div>

    <!-- Filtros -->
    <div class="filters-section">
      <div class="search-box">
        <i class="fas fa-search search-icon"></i>
        <input
          v-model="filters.search"
          @input="debouncedSearch"
          type="text"
          placeholder="Buscar módulos..."
          class="search-input"
        >
      </div>
      <div class="filter-controls">
        <select v-model="filters.is_core" @change="loadModules" class="form-select">
          <option value="">Todos los tipos</option>
          <option value="1">Solo Core</option>
          <option value="0">Solo Personalizados</option>
        </select>
        <select v-model="filters.is_active" @change="loadModules" class="form-select">
          <option value="">Todos los estados</option>
          <option value="1">Solo Activos</option>
          <option value="0">Solo Inactivos</option>
        </select>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="loading-container">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Cargando...</span>
      </div>
      <p class="mt-2">Cargando módulos...</p>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="alert alert-danger">
      <i class="fas fa-exclamation-triangle me-2"></i>
      {{ error }}
    </div>

    <!-- Content -->
    <div v-else class="content-section">
      <!-- Empty State -->
      <div v-if="modules.length === 0" class="empty-state">
        <i class="fas fa-puzzle-piece empty-icon"></i>
        <h3>No hay módulos</h3>
        <p>No se encontraron módulos con los filtros aplicados</p>
      </div>

      <!-- Módulos Grid -->
      <div v-else class="modules-grid">
        <div 
          v-for="modulo in modules" 
          :key="modulo.id" 
          class="module-card"
          :class="{ 'module-inactive': !modulo.is_active }"
        >
          <div class="module-header">
            <div class="module-info">
              <h3 class="module-title">{{ modulo.name }}</h3>
              <p class="module-code">{{ modulo.code }}</p>
            </div>
            <div class="module-toggle">
              <div class="form-check form-switch">
                <input 
                  class="form-check-input" 
                  type="checkbox" 
                  :id="`switch-${modulo.id}`"
                  v-model="modulo.is_active"
                  @change="toggleModule(modulo)"
                  :disabled="updating === modulo.id"
                >
                <label class="form-check-label" :for="`switch-${modulo.id}`">
                  {{ modulo.is_active ? 'Activo' : 'Inactivo' }}
                </label>
              </div>
            </div>
          </div>
          
          <div class="module-body">
            <p class="module-description">{{ modulo.description || 'Sin descripción' }}</p>
            
            <div class="module-badges">
              <span class="badge" :class="{
                'bg-danger': modulo.is_core,
                'bg-secondary': !modulo.is_core
              }">
                {{ modulo.is_core ? 'Core' : 'Personalizado' }}
              </span>
              
              <span class="badge" :class="{
                'bg-success': modulo.is_active,
                'bg-warning': !modulo.is_active
              }">
                {{ modulo.is_active ? 'Activo' : 'Inactivo' }}
              </span>
            </div>
          </div>
          
          <div class="module-footer">
            <small class="text-muted">
              <i class="fas fa-calendar me-1"></i>
              Creado: {{ formatDate(modulo.created_at) }}
            </small>
          </div>
          
          <!-- Loading overlay -->
          <div v-if="updating === modulo.id" class="module-loading">
            <div class="spinner-border text-primary" role="status">
              <span class="visually-hidden">Actualizando...</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Información total -->
      <div class="pagination-container">
        <div class="pagination-info">
          Mostrando {{ totalModules }} módulos en total
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import axios from 'axios'

export default {
  name: 'ModulosActivacion',
  setup() {
    // State
    const modules = ref([])
    const loading = ref(false)
    const error = ref('')
    const updating = ref(null)
    const stats = ref(null)
    
    // Filters
    const filters = ref({
      search: '',
      is_core: '',
      is_active: ''
    })
    
    // Pagination
    const currentPage = ref(1)
    const totalPages = ref(1)
    const totalModules = ref(0)
    const itemsPerPage = 50  // Aumentar para obtener todos los módulos

    // Computed
    const startRecord = computed(() => {
      return (currentPage.value - 1) * itemsPerPage + 1
    })

    const endRecord = computed(() => {
      const end = currentPage.value * itemsPerPage
      return end > totalModules.value ? totalModules.value : end
    })

    // Methods
    const loadModules = async () => {
      try {
        loading.value = true
        error.value = ''
        
        // Para la activación de módulos, obtener TODOS los módulos sin paginación
        const params = new URLSearchParams({
          per_page: 100,  // Obtener todos los módulos
          ...filters.value
        })

        const response = await axios.get(`/api/companies-register/modulos-disponibles?${params}`)
        
        if (response.data && response.data.data) {
          modules.value = response.data.data
          totalPages.value = 1  // Solo una página
          totalModules.value = response.data.pagination ? response.data.pagination.total : response.data.data.length
          
          // Load stats
          loadStats()
        }
      } catch (err) {
        console.error('Error loading modules:', err)
        error.value = 'Error al cargar los módulos'
      } finally {
        loading.value = false
      }
    }

    const loadStats = async () => {
      try {
        // Para estadísticas, obtener todos los módulos sin paginación
        const response = await axios.get(`/api/companies-register/modulos-disponibles?per_page=100`)
        
        if (response.data && response.data.data) {
          const allModules = response.data.data
          stats.value = {
            total_modules: response.data.pagination ? response.data.pagination.total : allModules.length,
            active_modules: allModules.filter(m => m.is_active).length,
            inactive_modules: allModules.filter(m => !m.is_active).length,
            core_modules: allModules.filter(m => m.is_core).length
          }
        }
      } catch (err) {
        console.error('Error loading stats:', err)
      }
    }

    const toggleModule = async (modulo) => {
      try {
        updating.value = modulo.id
        
        const response = await axios.put(`/api/companies-register/modulos-disponibles/${modulo.id}`, {
          is_active: modulo.is_active
        })
        
        if (response.data && response.data.success) {
          // Update stats
          loadStats()
        } else {
          // Revert change on error
          modulo.is_active = !modulo.is_active
          error.value = 'Error al actualizar el módulo'
        }
      } catch (err) {
        console.error('Error toggling module:', err)
        // Revert change on error
        modulo.is_active = !modulo.is_active
        error.value = 'Error al actualizar el módulo'
      } finally {
        updating.value = null
      }
    }

    const refreshModules = () => {
      loadModules()
    }

    const changePage = (page) => {
      if (page >= 1 && page <= totalPages.value) {
        currentPage.value = page
        loadModules()
      }
    }

    // Debounced search
    let searchTimeout = null
    const debouncedSearch = () => {
      clearTimeout(searchTimeout)
      searchTimeout = setTimeout(() => {
        currentPage.value = 1
        loadModules()
      }, 500)
    }

    const formatDate = (date) => {
      if (!date) return '-'
      return new Date(date).toLocaleDateString('es-ES')
    }

    // Initialize
    onMounted(() => {
      loadModules()
    })

    return {
      modules,
      loading,
      error,
      updating,
      stats,
      filters,
      currentPage,
      totalPages,
      totalModules,
      startRecord,
      endRecord,
      loadModules,
      toggleModule,
      refreshModules,
      changePage,
      debouncedSearch,
      formatDate
    }
  }
}
</script>

<style>
.activacion-modulos-container {
  padding: 1.5rem;
  background-color: var(--secondary-bg-main);
  color: var(--text-primary);
  min-height: calc(100vh - 4rem);
  max-width: 1400px;
  margin: 0 auto;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid var(--border-color);
}

.page-title {
  margin: 0;
  font-size: 2rem;
  font-weight: 700;
  color: var(--text-primary);
  display: flex;
  align-items: center;
}

.page-subtitle {
  margin: 0.5rem 0 0 0;
  color: var(--text-secondary);
  font-size: 1rem;
  display: flex;
  align-items: center;
}

.btn-secondary-custom {
  background: var(--card-bg);
  border: 1px solid var(--border-color);
  color: var(--text-secondary);
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 500;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.btn-secondary-custom:hover {
  background: var(--secondary-bg-light);
  color: var(--text-primary);
  transform: translateY(-1px);
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 2rem;
}

.stat-card {
  background: var(--card-bg);
  padding: 1.5rem;
  border-radius: 12px;
  text-align: center;
  border: 1px solid var(--border-color);
  box-shadow: var(--card-shadow);
  transition: all 0.2s ease;
}

.stat-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
}

.stat-number {
  font-size: 2rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
}

.stat-label {
  color: var(--text-secondary);
  font-size: 0.875rem;
  font-weight: 500;
}

.stat-total .stat-number { color: var(--primary-color); }
.stat-active .stat-number { color: var(--success-color); }
.stat-inactive .stat-number { color: var(--warning-color); }
.stat-core .stat-number { color: var(--danger-color); }

.filters-section {
  background: var(--card-bg);
  padding: 1.5rem;
  border-radius: 12px;
  margin-bottom: 2rem;
  border: 1px solid var(--border-color);
  box-shadow: var(--card-shadow);
  display: flex;
  gap: 1rem;
  align-items: center;
  flex-wrap: wrap;
}

.search-box {
  position: relative;
  flex: 1;
  min-width: 250px;
}

.search-icon {
  position: absolute;
  left: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  color: var(--text-secondary);
  font-size: 0.875rem;
  z-index: 2;
  pointer-events: none;
}

.search-input {
  width: 100%;
  padding: 0.75rem 1rem 0.75rem 2.75rem;
  border: 1px solid var(--border-color);
  border-radius: 6px;
  font-size: 0.875rem;
  transition: all 0.2s ease;
  background-color: var(--input-bg);
  color: var(--text-primary);
}

.search-input:focus {
  outline: none;
  border-color: var(--primary-color);
  box-shadow: 0 0 0 0.2rem rgba(59, 130, 246, 0.25);
}

.search-input::placeholder {
  color: var(--input-placeholder);
}

.filter-controls {
  display: flex;
  gap: 1rem;
}

.form-select {
  padding: 0.75rem;
  border: 1px solid var(--border-color);
  border-radius: 6px;
  background-color: var(--input-bg);
  color: var(--text-primary);
  min-width: 180px;
  font-size: 0.875rem;
  transition: all 0.2s ease;
}

.form-select:focus {
  border-color: var(--primary-color);
  box-shadow: 0 0 0 0.2rem rgba(59, 130, 246, 0.25);
  background-color: var(--input-bg);
}

.loading-container {
  text-align: center;
  padding: 3rem;
  background: var(--card-bg);
  border-radius: 12px;
  border: 1px solid var(--border-color);
  box-shadow: var(--card-shadow);
  color: var(--text-primary);
}

.content-section {
  background: var(--card-bg);
  border-radius: 12px;
  padding: 1.5rem;
  border: 1px solid var(--border-color);
  box-shadow: var(--card-shadow);
  animation: fadeIn 0.3s ease-in-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.empty-state {
  text-align: center;
  padding: 3rem;
  color: var(--text-secondary);
}

.empty-state h3 {
  color: var(--text-primary);
  margin-bottom: 0.5rem;
  font-size: 1.25rem;
  font-weight: 600;
}

.empty-state p {
  margin: 0;
  font-size: 0.875rem;
}

.empty-icon {
  font-size: 3rem;
  margin-bottom: 1rem;
  opacity: 0.6;
  color: var(--text-secondary);
}

.modules-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 1rem;
  margin-bottom: 2rem;
}

.module-card {
  background: var(--card-bg);
  border: 1px solid var(--border-color);
  border-radius: 12px;
  padding: 1.25rem;
  transition: all 0.2s ease;
  position: relative;
  border-left: 3px solid var(--border-color);
  box-shadow: var(--card-shadow);
}

.module-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border-color: var(--primary-color);
}

.module-card:focus-within {
  border-color: var(--primary-color);
  box-shadow: 0 0 0 0.2rem rgba(59, 130, 246, 0.25);
}

.module-inactive {
  opacity: 0.6;
  border-left-color: var(--text-secondary);
}

.module-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1rem;
}

.module-title {
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 0.25rem 0;
}

.module-code {
  font-size: 0.8rem;
  color: var(--text-secondary);
  margin: 0;
  font-family: 'Monaco', 'Courier New', monospace;
  background: var(--secondary-bg-light);
  padding: 0.125rem 0.375rem;
  border-radius: 4px;
}

.module-toggle .form-check {
  margin: 0;
}

.form-switch .form-check-input {
  width: 2.5em;
  height: 1.3em;
  background-color: var(--input-bg);
  border-color: var(--border-color);
}

.form-switch .form-check-input:checked {
  background-color: var(--success-color);
  border-color: var(--success-color);
}

.form-switch .form-check-input:focus {
  box-shadow: 0 0 0 0.2rem rgba(34, 197, 94, 0.25);
}

.form-check-label {
  color: var(--text-primary);
  font-size: 0.875rem;
  font-weight: 500;
}

.module-description {
  color: var(--text-secondary);
  line-height: 1.5;
  margin-bottom: 1rem;
  font-size: 0.875rem;
}

.module-badges {
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1rem;
  flex-wrap: wrap;
}

.badge {
  padding: 0.25rem 0.5rem;
  font-size: 0.7rem;
  font-weight: 500;
}

.badge.bg-success {
  background-color: var(--success-color) !important;
}

.badge.bg-danger {
  background-color: var(--danger-color) !important;
}

.badge.bg-warning {
  background-color: var(--warning-color) !important;
  color: var(--warning-text) !important;
}

.badge.bg-secondary {
  background-color: var(--text-secondary) !important;
}

.module-footer {
  border-top: 1px solid var(--border-color);
  padding-top: 1rem;
  margin-top: 1rem;
  font-size: 0.75rem;
  color: var(--text-secondary);
}

.module-loading {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(var(--card-bg-rgb), 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
}

.pagination-container {
  border-top: 1px solid var(--border-color);
  padding-top: 1.5rem;
  margin-top: 2rem;
}

.pagination-info {
  text-align: center;
  margin-top: 0.5rem;
  color: var(--text-secondary);
  font-size: 0.875rem;
}

/* Alert overrides */
.alert {
  background: var(--card-bg);
  border: 1px solid var(--danger-color);
  color: var(--text-primary);
  border-radius: 8px;
  padding: 1rem;
}

.alert-danger {
  background: var(--danger-bg);
  border-color: var(--danger-color);
  color: var(--danger-text);
}

/* Spinner with theme support */
.spinner-border {
  color: var(--primary-color);
  width: 1.5rem;
  height: 1.5rem;
}

.text-primary .spinner-border {
  color: var(--primary-color);
}

/* Text utilities */
.text-muted {
  color: var(--text-secondary) !important;
}

.visually-hidden {
  position: absolute !important;
  width: 1px !important;
  height: 1px !important;
  padding: 0 !important;
  margin: -1px !important;
  overflow: hidden !important;
  clip: rect(0, 0, 0, 0) !important;
  white-space: nowrap !important;
  border: 0 !important;
}

/* Transiciones suaves */
.activacion-modulos-container * {
  transition: color 0.2s ease, background-color 0.2s ease, border-color 0.2s ease, transform 0.2s ease;
}

/* Variables CSS locales para compatibilidad */
:root {
  --card-bg-rgb: 255, 255, 255;
  --success-color: #22c55e;
  --warning-color: #f59e0b;
  --warning-text: #92400e;
  --warning-bg: rgba(251, 191, 36, 0.1);
  --danger-color: #ef4444;
  --danger-text: #dc2626;
  --danger-bg: rgba(239, 68, 68, 0.1);
}

[data-theme="dark"] {
  --card-bg-rgb: 31, 41, 55;
  --success-color: #4ade80;
  --warning-color: #fbbf24;
  --warning-text: #fcd34d;
  --warning-bg: rgba(251, 191, 36, 0.2);
  --danger-color: #f87171;
  --danger-text: #fca5a5;
  --danger-bg: rgba(239, 68, 68, 0.2);
}

@media (max-width: 768px) {
  .activacion-modulos-container {
    padding: 1rem;
  }
  
  .page-header {
    flex-direction: column;
    gap: 1rem;
    align-items: flex-start;
  }
  
  .filters-section {
    flex-direction: column;
    align-items: stretch;
    padding: 1rem;
  }
  
  .filter-controls {
    flex-direction: column;
    gap: 0.75rem;
  }
  
  .modules-grid {
    grid-template-columns: 1fr;
  }
  
  .stats-grid {
    grid-template-columns: repeat(2, 1fr);
  }
  
  .module-header {
    flex-direction: column;
    gap: 0.75rem;
  }
  
  .module-toggle {
    align-self: flex-start;
  }
}

@media (max-width: 576px) {
  .activacion-modulos-container {
    padding: 0.75rem;
  }
  
  .page-title {
    font-size: 1.5rem;
  }
  
  .page-subtitle {
    font-size: 0.875rem;
  }
  
  .stats-grid {
    grid-template-columns: 1fr;
  }
  
  .stat-number {
    font-size: 1.5rem;
  }
  
  .modules-grid {
    gap: 0.75rem;
  }
  
  .module-card {
    padding: 1rem;
  }
  
  .search-box {
    min-width: auto;
  }
  
  .btn-secondary-custom {
    padding: 0.5rem 1rem;
    font-size: 0.875rem;
  }
}
</style>
